import { memo } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { alpha } from "@mui/material/styles";
import type { BatchPersonSummary } from "./types";

type PersonBatchRegisterSummaryProps = {
  fileName: string;
  isChecking: boolean;
  summary: BatchPersonSummary | null;
};

type CountCardTone = "success" | "info" | "warning";

function CountCard({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: number;
  hint?: string;
  tone: CountCardTone;
}) {
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 1.5,
        flex: 1,
        minWidth: 110,
        borderRadius: 2,
        bgcolor: alpha(theme.palette[tone].main, 0.12),
        borderColor: theme.palette[tone].main,
        borderWidth: 1.5,
      })}
    >
      <Typography variant="h5" fontWeight={700} color={`${tone}.dark`}>
        {value}
      </Typography>
      <Typography variant="body2" fontWeight={600} color={`${tone}.dark`}>
        {label}
      </Typography>
      {hint && (
        <Typography variant="caption" color={`${tone}.dark`} sx={{ opacity: 0.8 }}>
          {hint}
        </Typography>
      )}
    </Paper>
  );
}

const PersonBatchRegisterSummary = memo(
  ({ fileName, isChecking, summary }: PersonBatchRegisterSummaryProps) => {
    if (isChecking || !summary) {
      return (
        <Stack alignItems="center" spacing={1.5} sx={{ py: 3 }}>
          <CircularProgress size={28} />
          <Typography variant="body2" color="text.secondary">
            Verificando quem já está cadastrado…
          </Typography>
        </Stack>
      );
    }

    const nothingToRegister = summary.toRegister.length === 0;

    return (
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          Arquivo: {fileName}
        </Typography>
       
        {summary.duplicates.length > 0 && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            Há CPFs repetidos no arquivo. Mantivemos a linha com mais informações.
          </Alert>
        )}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <CountCard
            tone="success"
            label="Prontos para cadastrar"
            value={summary.toRegister.length}
          />
          <CountCard
            tone="info"
            label="Já cadastrados"
            value={summary.alreadyInOrg.length}
            // hint="Não serão cadastrados de novo"
          />
          <CountCard
            tone="warning"
            label="Dados inválidos"
            value={summary.invalid.length}
          />
        </Box>
        {nothingToRegister && (
          <Alert severity="info">
            Todos os pacientes da lista já estão cadastrados.
          </Alert>
        )}
        {summary.duplicates.length > 0 && (
          <Stack spacing={0.5}>
            {summary.duplicates.slice(0, 8).map((item) => (
              <Typography
                key={`dup-${item.lineNumber}`}
                variant="body2"
                color="text.secondary"
              >
                Linha {item.lineNumber}
                {item.name ? ` — ${item.name}` : ""}: CPF repetido
              </Typography>
            ))}
            {summary.duplicates.length > 8 && (
              <Typography variant="caption" color="text.secondary">
                + {summary.duplicates.length - 8} repetidos
              </Typography>
            )}
          </Stack>
        )}
        {summary.invalid.length > 0 && (
          <Stack spacing={0.5}>
            {summary.invalid.slice(0, 8).map((item) => (
              <Typography
                key={`${item.lineNumber}-${item.reason}`}
                variant="body2"
                color="warning.dark"
              >
                Linha {item.lineNumber}
                {item.name ? ` — ${item.name}` : ""}: {item.reason}
              </Typography>
            ))}
            {summary.invalid.length > 8 && (
              <Typography variant="caption" color="text.secondary">
                + {summary.invalid.length - 8} itens com problema
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    );
  },
);

PersonBatchRegisterSummary.displayName = "PersonBatchRegisterSummary";

export default PersonBatchRegisterSummary;
